import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Box,
  Typography,
  Button,
  SvgIcon,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ModalDialogVideo from 'src/components/ModalDialogVideo/index';
import { STYLE_OPTIONS } from 'src/constants';

const fullWebinarStyles = {
  webinarCard: {
    padding: '24px',
    marginBottom: '30px',
    background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 50%, #155e75 100%)',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: STYLE_OPTIONS.FONT_FAMILY,

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
      zIndex: 0,
    },
  },

  contentWrapper: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },

  textContent: {
    flex: '1 1 300px',
    minWidth: '300px',
  },

  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  },

  subtitle: {
    fontSize: '18px',
    marginBottom: '16px',
    opacity: 0.9,
    lineHeight: 1.4,
  },

  description: {
    fontSize: '14px',
    opacity: 0.8,
    lineHeight: 1.5,
    fontStyle: 'italic',
  },

  buttonContent: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },

  videoButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    padding: '12px 24px',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: 'bold',
    textTransform: 'none',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
    },
  },

  newBadge: {
    backgroundColor: '#ff4757',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(255, 71, 87, 0.4)',
    animation: 'pulse 2s infinite',
  },

  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.05)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },

  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

function FullWebinarSection({
  classes,
  videoUrl,
  videoDescription,
}) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Card className={classes.webinarCard}>
        <Box className={classes.contentWrapper}>
          <Box className={classes.textContent}>
            <Typography className={classes.title}>
              Making the Switch to CP Texting - Full Webinar
            </Typography>
            <Typography className={classes.subtitle}>
              Watch this video especially if you have been using CP
              with PB and want to understand how the new built-in
              CP Texting works!
            </Typography>
            {videoDescription && (
              <Typography className={classes.description}>
                {videoDescription}
              </Typography>
            )}
          </Box>
          <Box className={classes.buttonContent}>
            <Box className={classes.newBadge}>
              New Video
            </Box>
            <Button
              className={classes.videoButton}
              onClick={handleOpen}
              size="large"
            >
              <Box className={classes.iconWrapper}>
                <SvgIcon component={PlayArrowIcon} />
                Watch Full Webinar
                <SvgIcon component={OndemandVideoIcon} />
              </Box>
            </Button>
          </Box>
        </Box>
      </Card>

      <ModalDialogVideo
        onClose={handleClose}
        isVisible={open}
        videosrc={videoUrl}
        title="Making the Switch to CP Texting - Full Webinar"
        largeModal
      />
    </>
  );
}

FullWebinarSection.propTypes = {
  classes: PropTypes.object.isRequired,
  videoUrl: PropTypes.string.isRequired,
  videoDescription: PropTypes.string,
};

FullWebinarSection.defaultProps = {
  videoDescription: '',
};

export default withStyles(FullWebinarSection, fullWebinarStyles);
